"use client";

import React, { useRef, useEffect, useState } from "react";
import { motion, useAnimationControls, PanInfo, useMotionValue } from "framer-motion";
import { Star } from "lucide-react";

interface Testimonial {
  id: number;
  name: string;
  role: string;
  content: string;
}

const TESTIMONIALS_ROW_1: Testimonial[] = [
  {
    id: 1,
    name: "Alexander von Berg",
    role: "Architectural Designer",
    content: "The Apollo G3 is not just a device; it's a statement. It transformed how I interact with my environment.",
  },
  {
    id: 2,
    name: "Elena Rossi",
    role: "Interior Stylist",
    content: "Elegance meets intelligence. Finally, a smart hub that respects the aesthetic of a curated home.",
  },
  {
    id: 3,
    name: "Julian Thorne",
    role: "Tech Collector",
    content: "The privacy-first approach is the ultimate luxury. Knowing my home remains mine is priceless.",
  },
  {
    id: 4,
    name: "Sophia Chen",
    role: "Creative Director",
    content: "Seamless, intuitive, and breathtakingly beautiful. It's the centerpiece I didn't know I needed.",
  },
];

const TESTIMONIALS_ROW_2: Testimonial[] = [
  {
    id: 5,
    name: "Marcus Sterling",
    role: "Private Investor",
    content: "The attention to detail in the interface is parallel to a Swiss timepiece. Absolute perfection.",
  },
  {
    id: 6,
    name: "Isabella Moretti",
    role: "Fine Art Curator",
    content: "It blends into the background when quiet, and commands attention when called. True sophistication.",
  },
  {
    id: 7,
    name: "David H. Grant",
    role: "Estate Manager",
    content: "Reliability is the greatest luxury. Apollo G3 delivers a flawless performance day after day.",
  },
  {
    id: 8,
    name: "Claire de Lune",
    role: "Lighting Designer",
    content: "The way it orchestrates light is pure poetry. It understands mood better than any human system.",
  },
];

const Card = ({ testimonial }: { testimonial: Testimonial }) => (
  <div className="flex-shrink-0 w-[380px] sm:w-[480px] p-10 mx-6 rounded-[2rem] bg-white/5 backdrop-blur-xl border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.1)] transition-colors hover:bg-white/[0.08]">
    <div className="flex gap-1 mb-8 text-primary/80">
      {[...Array(5)].map((_, i) => (
        <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
      ))}
    </div>
    <p className="text-xl sm:text-2xl font-sans text-foreground/90 leading-snug mb-10 font-light tracking-tight">
      "{testimonial.content}"
    </p>
    <div className="flex items-center gap-4">
      <div className="h-px w-8 bg-primary/30" />
      <div>
        <h4 className="text-2xl font-serif text-foreground tracking-tight italic">{testimonial.name}</h4>
        <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-1.5 font-bold">{testimonial.role}</p>
      </div>
    </div>
  </div>
);

const MarqueeRow = ({ items, direction = "left", speed = 40 }: { items: Testimonial[], direction?: "left" | "right", speed?: number }) => {
  const [isPaused, setIsPaused] = useState(false);
  const x = useMotionValue(0);
  const controls = useAnimationControls();
  
  // Duplicating items for infinite loop
  const duplicatedItems = [...items, ...items, ...items];

  useEffect(() => {
    const animate = async () => {
      await controls.start({
        x: direction === "left" ? "-33.33%" : "0%",
        transition: {
          duration: speed,
          ease: "linear",
          repeat: Infinity,
          repeatType: "loop",
        },
      });
    };

    if (!isPaused) {
      animate();
    } else {
      controls.stop();
    }
  }, [isPaused, controls, direction, speed]);

  return (
    <div 
      className="relative overflow-visible py-8 cursor-grab active:cursor-grabbing"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <motion.div
        className="flex w-max"
        animate={controls}
        drag="x"
        dragConstraints={{ left: -3000, right: 3000 }}
        dragElastic={0.1}
        initial={{ x: direction === "left" ? "0%" : "-33.33%" }}
        style={{ x }}
      >
        {duplicatedItems.map((item, idx) => (
          <Card key={`${item.id}-${idx}`} testimonial={item} />
        ))}
      </motion.div>
    </div>
  );
};

export function SocialProof() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1.2,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const rowLeftVariants = {
    hidden: { opacity: 0, x: -150 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 40,
        damping: 25,
        mass: 1.2,
      },
    },
  };

  const rowRightVariants = {
    hidden: { opacity: 0, x: 150 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 40,
        damping: 25,
        mass: 1.2,
      },
    },
  };

  return (
    <section id="reviews" className="py-32 lg:py-48 bg-background relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
      
      <motion.div
        className="relative mx-auto max-w-7xl px-6 lg:px-10 mb-24 text-center"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <motion.p 
          variants={itemVariants}
          className="text-[11px] uppercase tracking-[0.4em] text-primary/60 mb-8 font-bold"
        >
          Voices of Distinction
        </motion.p>
        <motion.h2 
          variants={itemVariants}
          className="text-4xl lg:text-7xl font-serif tracking-tight leading-[1.1] text-balance"
        >
          An orchestration of <br />
          <span className="italic font-normal opacity-80">flawless living.</span>
        </motion.h2>
      </motion.div>

      <div className="space-y-12 relative z-10">
        <motion.div
          variants={rowLeftVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <MarqueeRow items={TESTIMONIALS_ROW_1} direction="left" speed={80} />
        </motion.div>

        <motion.div
          variants={rowRightVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <MarqueeRow items={TESTIMONIALS_ROW_2} direction="right" speed={90} />
        </motion.div>
      </div>
    </section>
  );
}
